import React from 'react';

import { getEditorPreloader } from '@confluence-classic/confluence-fabric-editor/src/lib/index.ts';

import type { RouteMatch } from '@confluence/route/entry-points/Route.ts';
import { PageShownPublicLinksEventLoader } from '@confluence/external-share-utils/entry-points/PageShownPublicLinksEventLoader.tsx';
import { useIsEmbeddedConfluence_DO_NOT_USE } from '@confluence/embedded-confluence-check';
import { getUniquePageLoadId } from '@confluence/unique-page-load-id/entry-points/UniquePageLoadId.ts';
import { END } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { PRODUCT_HOME_ID } from '@confluence/app-navigation/entry-points/navigationIDs.ts';
import { HubEditorWrapper } from '@confluence/company-hub/entry-points/hub-layout.ts';
import { HeadingLinksStyleWrapper } from '@confluence/editor-heading-links/entry-points/HeadingLinksStyleWrapper.ts';
import { ConfluenceDependenciesProvider } from '@confluence/custom-sites-extensions/entry-points/ConfluenceDependenciesProvider.ts';
import { ExtensionMode } from '@atlassian/company-hub-common';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import HubEditor from '@atlassian/hub-editor';

export const HubEditorRoute = ({
	params: { contentId, contentType: editRouteTypeParam },
}: RouteMatch) => {
	const sessionData = useSessionData();

	// Don't render until session data is available to prevent context errors
	if (!sessionData) {
		return null;
	}

	return (
		<HubEditorWrapper>
			<ConfluenceDependenciesProvider mode={ExtensionMode.EDITOR} contentId={contentId}>
				<HeadingLinksStyleWrapper>
					<>
						<HubEditor
							contentId={contentId}
							product="CONFLUENCE"
							hasEditPermission
							viewMode="edit"
							userPreferencesProvider={undefined}
							isLoomPluginEnabled={false}
							editorPluginAIOptions={undefined}
							editorPluginAIDefinitionsEnabled={false}
							editorPluginAIDefinitionsConfig={undefined}
							isDesktop={false}
							contextPanelOptions={undefined}
							getExtensionHeight={undefined}
							__rendererExtensionOptions={undefined}
							PropsProvider={({ children }) => <>{children}</>}
						/>
						<PageShownPublicLinksEventLoader
							contentId={contentId}
							isPage={editRouteTypeParam === 'page'}
							isEditPage
						/>
					</>
				</HeadingLinksStyleWrapper>
			</ConfluenceDependenciesProvider>
		</HubEditorWrapper>
	);
};

HubEditorRoute.NAVIGATION_PARAMS = ({
	name: routeName,
	params: { contentId, spaceKey },
}: RouteMatch) => {
	// eslint-disable-next-line react-hooks/rules-of-hooks
	const isEmbeddedConfluence = useIsEmbeddedConfluence_DO_NOT_USE();
	const showPremiumOnboarding = !isEmbeddedConfluence;

	const contentType = 'page';

	const viewId = PRODUCT_HOME_ID;
	const screenEventName = 'companyHubEditorScreen';
	const screenIsInSpace = false;

	return {
		Screen: {
			screenEvent: {
				name: screenEventName,
				id: spaceKey,
				attributes: {
					isPublicView: false,
					pageLoadInfo: getUniquePageLoadId(),
					navdexPointType: END,
					screenIsInSpace,
				},
			},
			pageState: {
				routeName,
				spaceKey,
				contentId,
				contentType,
			},
			showPremiumOnboarding,
		},
		MainLayout: {
			navView: viewId,
			spaceKey,
			editorPreloader: getEditorPreloader,
			disableMinWidth: true,
			isViewPage: true,
			isEdit: true,
		},
	};
};
